<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import Icon from "./Icon.vue";
import Button from "./Button.vue";
import { remaining } from "../api/mappers";
import { t } from "../i18n";
import { vTooltip } from "../directives/tooltip";
const props = defineProps<{
  endsAt: string | null;
  freeUntil?: string | null;
  templates?: boolean;
  /** "chip": compact clock shown while the window is open; "bar": full-width notice shown once it closed. */
  mode?: "chip" | "bar" | "full";
}>();
const emit = defineEmits<{ template: [] }>();
const now = ref(new Date());
let timer: ReturnType<typeof setInterval> | undefined;
onMounted(() => {
  timer = setInterval(() => (now.value = new Date()), 30_000);
});
onBeforeUnmount(() => clearInterval(timer));
const free = computed(() => remaining(props.freeUntil ?? null, now.value));
const left = computed(() => remaining(props.endsAt, now.value));
const expired = computed(() => !left.value && !free.value);
const closingSoon = computed(() => {
  const end = props.freeUntil ?? props.endsAt;
  if (!end) return false;
  const ms = new Date(end).getTime() - now.value.getTime();
  return ms > 0 && ms < 60 * 60_000;
});
const mode = computed(() => props.mode ?? "full");
</script>
<template>
  <span
    v-if="mode === 'chip' && !expired"
    class="window-chip"
    :class="{ free: !!free, closing: closingSoon }"
    v-tooltip="
      free
        ? t('inbox.window.free', { time: free })
        : t('inbox.window.remaining', { time: left ?? '' })
    "
  >
    <Icon name="clock" :size="14" />
    <template v-if="free">{{
      t("inbox.window.free_chip", { time: free })
    }}</template>
    <template v-else>{{
      t("inbox.window.chip", { time: left ?? "" })
    }}</template>
  </span>
  <div
    v-else-if="mode !== 'chip' && (mode === 'full' || expired)"
    class="window-bar"
    :class="{ expired, free: !!free }"
  >
    <Icon :name="expired ? 'lock' : 'clock'" :size="16" />
    <span class="grow">
      <template v-if="free">{{
        t("inbox.window.free", { time: free })
      }}</template>
      <template v-else-if="left">{{
        t("inbox.window.remaining", { time: left })
      }}</template>
      <template v-else>{{ t("inbox.window.expired") }}</template>
    </span>
    <Button
      v-if="expired && templates"
      variant="secondary"
      compact
      @click="emit('template')"
      >{{ t("inbox.window.send_template") }}</Button
    >
  </div>
</template>
